import type {
  IListCriteriaGroup,
  IListField,
  IListFieldCatalogue,
  IListFormattingRule,
  IListQuery,
  ISavedView,
  ListCriteriaNode,
  ListFieldKind
} from './api/lists-contracts';
import {
  dateTokenLabel,
  formatListValue,
  operatorArity,
  operatorLabel,
  operatorsFor,
  readDateToken
} from './lists';

/**
 * A list's filter — the criteria tree as Lists and Reports read, edit, say and send it (SR-230,
 * design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md` §3.4 and §3.8), over
 * survey-api's grammar (`ListCriteriaParser`, `ListQueryBinder`; its design §4).
 *
 * **One tree, three doors.** The chips, every column's funnel and *Advanced…* edit the one tree
 * this module holds: a funnel edits **the root `and` group's own nodes on its column**
 * (`rootLeafFor` / `withRootLeaf`, SR-221's rule) and nothing nested; *Advanced…* edits it whole.
 *
 * **Nothing here judges a condition.** survey-api's binder decides whether a field names anything,
 * whether an operator fits its kind and whether a value is of the shape; what is typed is held as
 * typed and sent so, and its 400 lands on the node it names (`placeProblems`). The rejected
 * alternative — refusing `abc` in a number box before the read — would be a second binder that
 * agrees with survey-api's until one of them moves (the concern criteria's rule, `concern-predicate.ts`).
 *
 * - **`readCriteria` is total and drops nothing.** It reads the parser's structural grammar — a
 *   group `{op: and | or, children}` (the root with `version`), a leaf `{field, op, value?}` — into
 *   nodes carrying ids for the visit; any other shape (an unknown key, a `children` that is no
 *   array, a leaf with no field) becomes one `raw` node holding the stored JSON **verbatim**, said
 *   as "a condition the words cannot say" and written back exactly as read. A leaf on a field or
 *   operator this app has no word for is still a leaf, said as spelled.
 * - **`writeCriteria` composes, it does not refuse.** The tree the wire takes, `version: 1` at the
 *   root, no ids. A number kind's typed text goes as a JSON number only where it is plain decimal
 *   text (the dollar sign, grouping commas, spaces and a trailing `%` set aside) and as typed where
 *   not, for survey-api to answer at `…value` (the team firms' rule — `1e5` is not 100,000).
 * - **The one evaluation here is a view's formatting rule** (`evaluateFormatting`): survey-api
 *   stores those "shape only; the screen evaluates them" (its design §5), with its §4's blank and
 *   type rules. A date token in a rule is never resolved here — it matches nothing (see there).
 */

// ---- The tree ----------------------------------------------------------------------------------

/** A condition. `field` and `op` are `''` while the builder has none chosen; `value` is what the
 *  controls hold — `undefined` where the operator takes none, typed text for a number, a list for
 *  `in`, a pair for `between` — or what the stored leaf carried, untouched. */
export interface ICriteriaLeaf {
  id: string;
  type: 'leaf';
  field: string;
  op: string;
  value?: unknown;
}

/** A group, `and` or `or`, to any depth. */
export interface ICriteriaGroup {
  id: string;
  type: 'group';
  op: 'and' | 'or';
  children: readonly CriteriaNode[];
}

/** A stored node outside the grammar this module reads, kept verbatim — never dropped. */
export interface ICriteriaRaw {
  id: string;
  type: 'raw';
  stored: unknown;
}

export type CriteriaNode = ICriteriaLeaf | ICriteriaGroup | ICriteriaRaw;

/** Where survey-api answers a query's criteria (`ListQueryBinder`): every read, the export, a
 *  mass update and a saved view's body put it under `query`. */
export const CRITERIA_PATH = 'query.criteria';

/** `ListCriteriaParser.Version`. */
const CRITERIA_VERSION = 1;

/** The name box's field (`sys.display_name`, survey-api's `FixedFields`) — *Name contains…*. */
export const NAME_FIELD = 'sys.display_name';

let lastId = 0;

/** A node id, unique for the page load — a React key, and the key problems are placed by. */
function nextId(): string {
  lastId += 1;
  return `criteria-${lastId}`;
}

/** An empty root: every row. */
export function emptyRoot(): ICriteriaGroup {
  return { id: nextId(), type: 'group', op: 'and', children: [] };
}

/** A blank condition — no field, no operator, no value. */
export function newLeaf(): ICriteriaLeaf {
  return { id: nextId(), type: 'leaf', field: '', op: '' };
}

/** A leaf on a field, under a fresh id; `value` absent where the operator takes none. */
export function leafOf(field: string, op: string, value?: unknown): ICriteriaLeaf {
  return value === undefined
    ? { id: nextId(), type: 'leaf', field, op }
    : { id: nextId(), type: 'leaf', field, op, value };
}

/** A group holding the given children, or one blank condition — the parser refuses an empty
 *  nested group, so none is drawn. */
export function newGroup(op: 'and' | 'or', children?: readonly CriteriaNode[]): ICriteriaGroup {
  return {
    id: nextId(),
    type: 'group',
    op,
    children: children && children.length > 0 ? children : [newLeaf()]
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function onlyKeys(record: Record<string, unknown>, known: readonly string[]): boolean {
  return Object.keys(record).every((key) => known.includes(key));
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value !== '';
}

function raw(stored: unknown): ICriteriaRaw {
  return { id: nextId(), type: 'raw', stored };
}

/** A copy of a stored value, so an edit of the tree never reaches the read it came from. */
function copied(value: unknown): unknown {
  return value === undefined ? undefined : (JSON.parse(JSON.stringify(value)) as unknown);
}

/** A node — dispatched as the parser does: `children`, or an `op` of `and` / `or`, is a group. */
function readNode(value: unknown): CriteriaNode {
  if (!isRecord(value)) return raw(copied(value));
  const groupish = 'children' in value || value.op === 'and' || value.op === 'or';
  return groupish ? readGroup(value, false) : readLeaf(value);
}

function readLeaf(value: Record<string, unknown>): CriteriaNode {
  const { field, op } = value;
  if (!onlyKeys(value, ['field', 'op', 'value']) || !nonEmptyString(field) || !nonEmptyString(op)) {
    return raw(copied(value));
  }
  return 'value' in value && value.value !== null
    ? { id: nextId(), type: 'leaf', field, op, value: copied(value.value) }
    : { id: nextId(), type: 'leaf', field, op };
}

function readGroup(value: Record<string, unknown>, isRoot: boolean): CriteriaNode {
  const keys = isRoot ? ['version', 'op', 'children'] : ['op', 'children'];
  const { op, children } = value;
  const readable =
    onlyKeys(value, keys) &&
    (op === 'and' || op === 'or') &&
    Array.isArray(children) &&
    (isRoot || children.length > 0);
  if (!readable) return raw(copied(value));
  return { id: nextId(), type: 'group', op, children: children.map(readNode) };
}

/**
 * The wire's tree as the builder's root, ids given for the visit. An absent tree is an empty root
 * (every row). A root that is no readable group — survey-api's parser refuses one, so only a store
 * written past it could hold one — is kept as one `raw` child of an empty root, said and sent back
 * as read.
 */
export function readCriteria(wire: unknown): ICriteriaGroup {
  if (wire === null || wire === undefined) return emptyRoot();
  const node = isRecord(wire) ? readGroup(wire, true) : raw(copied(wire));
  if (node.type === 'group') return node;
  return { ...emptyRoot(), children: [node] };
}

/** A field's kind, by code — the catalogue's, or `undefined` for a field it does not offer. */
export type KindOf = (code: string) => ListFieldKind | undefined;

/** Plain decimal text, once `$`, grouping commas, spaces and a trailing `%` are set aside —
 *  presentation, never validation; `1e5`, `0x10` and `12 %%` are not. */
export function plainNumber(text: string): Nullable<number> {
  const bare = text.replace(/[$,\s]/g, '').replace(/%$/, '');
  return /^-?(?:\d+(?:\.\d*)?|\.\d+)$/.test(bare) ? Number(bare) : null;
}

/** The kinds whose values are numbers — a box reads them as typed digits. */
export const NUMBER_KINDS: readonly string[] = ['number', 'money', 'percent'];

/** One value as sent: a number kind's text as a number where it reads as one, else as held. */
function sentValue(value: unknown, numeric: boolean): unknown {
  if (numeric && typeof value === 'string') return plainNumber(value) ?? value;
  return value;
}

/** One leaf as the wire spells it — also a formatting rule's `field`, `op` and `value`. */
export function writeLeaf(leaf: ICriteriaLeaf, kindOf: KindOf): Record<string, unknown> {
  const numeric = NUMBER_KINDS.includes(kindOf(leaf.field) ?? '');
  const value = Array.isArray(leaf.value)
    ? leaf.value.map((one) => sentValue(one, numeric))
    : sentValue(leaf.value, numeric);
  return {
    ...(leaf.field === '' ? {} : { field: leaf.field }),
    ...(leaf.op === '' ? {} : { op: leaf.op }),
    ...(value === undefined ? {} : { value })
  };
}

function writeNode(node: CriteriaNode, kindOf: KindOf): unknown {
  switch (node.type) {
    case 'raw':
      return copied(node.stored);
    case 'leaf':
      return writeLeaf(node, kindOf);
    case 'group':
      return { op: node.op, children: node.children.map((child) => writeNode(child, kindOf)) };
  }
}

/** The builder's root as the wire takes it — `version: 1`, no ids, a raw node as read. */
export function writeCriteria(root: ICriteriaGroup, kindOf: KindOf): IListCriteriaGroup {
  return {
    version: CRITERIA_VERSION,
    op: root.op,
    children: root.children.map((child) => writeNode(child, kindOf) as ListCriteriaNode)
  };
}

/** Each node's JSON path in the tree `writeCriteria` sends — positions are kept one for one, so a
 *  node's path is its place: `query.criteria.children[2]`. */
export function criteriaPaths(
  root: ICriteriaGroup,
  prefix: string = CRITERIA_PATH
): ReadonlyMap<string, string> {
  const paths = new Map<string, string>();
  const visit = (node: CriteriaNode, path: string) => {
    paths.set(node.id, path);
    if (node.type === 'group') {
      node.children.forEach((child, index) => visit(child, `${path}.children[${index}]`));
    }
  };
  visit(root, prefix);
  return paths;
}

/** Every leaf, in reading order — the builder numbers its rows by it. */
export function leavesOf(root: ICriteriaGroup): ICriteriaLeaf[] {
  const leaves: ICriteriaLeaf[] = [];
  const visit = (node: CriteriaNode) => {
    if (node.type === 'leaf') leaves.push(node);
    else if (node.type === 'group') node.children.forEach(visit);
  };
  visit(root);
  return leaves;
}

/** The tree with one node replaced — or, for `null`, removed — wherever it sits. A nested group
 *  emptied by a removal keeps one blank condition, since the parser refuses an empty one; the
 *  root may be empty (every row). */
export function replaceNode(
  root: ICriteriaGroup,
  id: string,
  next: Nullable<CriteriaNode>
): ICriteriaGroup {
  const walk = (group: ICriteriaGroup, isRoot: boolean): ICriteriaGroup => {
    const children = group.children.flatMap((child): CriteriaNode[] => {
      if (child.id === id) return next ? [next] : [];
      return child.type === 'group' ? [walk(child, false)] : [child];
    });
    return { ...group, children: !isRoot && children.length === 0 ? [newLeaf()] : children };
  };
  if (root.id === id) return next && next.type === 'group' ? next : emptyRoot();
  return walk(root, true);
}

export function removeNode(root: ICriteriaGroup, id: string): ICriteriaGroup {
  return replaceNode(root, id, null);
}

/** The node with `id`, wherever it sits. */
export function findNode(root: ICriteriaGroup, id: string): CriteriaNode | undefined {
  if (root.id === id) return root;
  for (const child of root.children) {
    if (child.id === id) return child;
    if (child.type === 'group') {
      const found = findNode(child, id);
      if (found) return found;
    }
  }
  return undefined;
}

// ---- The column's own conditions — the funnel's -----------------------------------------------

/** The name box's leaf: `sys.display_name contains …`, a root leaf of its own beside Name's
 *  funnel (Conga keeps the two apart too). */
function isNameBox(node: CriteriaNode): boolean {
  return node.type === 'leaf' && node.field === NAME_FIELD && node.op === 'contains';
}

/**
 * Whether a root child is the column's own — what its funnel reads and writes: a leaf on the
 * column, or an `or` group whose every child is a leaf on it (the funnel's "these values, or
 * blank"). The name box's leaf is never Name's funnel's.
 */
function isColumnOwn(node: CriteriaNode, field: string): boolean {
  if (node.type === 'leaf') return node.field === field && !isNameBox(node);
  if (node.type !== 'group' || node.op !== 'or' || node.children.length === 0) return false;
  return node.children.every((child) => child.type === 'leaf' && child.field === field);
}

function mentions(node: CriteriaNode, field: string): boolean {
  if (node.type === 'leaf') return node.field === field;
  if (node.type === 'group') return node.children.some((child) => mentions(child, field));
  return false;
}

/** What a column's funnel sees of the tree: the root's own nodes on it, or — `nested` — that a
 *  condition on it sits inside a nested group, *Advanced…*'s, shown and not edited there. */
export interface IColumnFilter {
  nodes: readonly CriteriaNode[];
  nested: boolean;
}

export function rootLeafFor(root: ICriteriaGroup, field: string): Nullable<IColumnFilter> {
  if (root.op === 'and') {
    const own = root.children.filter((child) => isColumnOwn(child, field));
    if (own.length > 0) return { nodes: own, nested: false };
  }
  const nested = root.children.filter((child) => !isNameBox(child) && mentions(child, field));
  return nested.length > 0 ? { nodes: nested, nested: true } : null;
}

/**
 * The root with the column's own nodes replaced by `nodes` — at the first one's place, or
 * appended — or, for `null` or `[]`, removed. Nested groups are never touched: they are
 * *Advanced…*'s. An `or` root has no column of its own — a funnel there would change the filter's
 * meaning — so it is wrapped first: the old root becomes the new `and` root's first child.
 */
export function withRootLeaf(
  root: ICriteriaGroup,
  field: string,
  nodes: Nullable<readonly CriteriaNode[]>
): ICriteriaGroup {
  const base: ICriteriaGroup =
    root.op === 'and' || root.children.length === 0
      ? { ...root, op: 'and' }
      : { ...emptyRoot(), children: [{ ...root, id: nextId() }] };
  const placed = nodes ?? [];
  const first = base.children.findIndex((child) => isColumnOwn(child, field));
  const kept = base.children.filter((child) => !isColumnOwn(child, field));
  // Every child before the first own one is kept, so its place in `kept` is its place before.
  if (first === -1) return { ...base, children: [...kept, ...placed] };
  return { ...base, children: [...kept.slice(0, first), ...placed, ...kept.slice(first)] };
}

/** The name box's text, or `''`. */
export function nameContainsOf(root: ICriteriaGroup): string {
  const leaf = root.op === 'and' ? root.children.find(isNameBox) : undefined;
  return leaf?.type === 'leaf' && typeof leaf.value === 'string' ? leaf.value : '';
}

/** The root with the name box's leaf set to `text`, or removed for `''`. */
export function withNameContains(root: ICriteriaGroup, text: string): ICriteriaGroup {
  const base: ICriteriaGroup =
    root.op === 'and' || root.children.length === 0
      ? { ...root, op: 'and' }
      : { ...emptyRoot(), children: [{ ...root, id: nextId() }] };
  const index = base.children.findIndex(isNameBox);
  if (text === '') return { ...base, children: base.children.filter((child) => !isNameBox(child)) };
  const leaf = leafOf(NAME_FIELD, 'contains', text);
  if (index === -1) return { ...base, children: [leaf, ...base.children] };
  const children = [...base.children];
  children[index] = { ...leaf, id: children[index].id };
  return { ...base, children };
}

/** How a column is filtered from its funnel (§3.4): ticked values, a range, dates, a text test
 *  (a long text — SR-408's R4: its values are too long to tick; a filter's value suggests them),
 *  or not at all — a summary filters on nothing. */
export type LeafTest = 'values' | 'range' | 'date' | 'text' | 'none';

export function leafTest(kind: ListFieldKind): LeafTest {
  switch (kind) {
    case 'long_text':
      return 'text';
    case 'text':
    case 'enum':
    case 'multi_enum':
    case 'bool':
    case 'person':
      return 'values';
    case 'number':
    case 'money':
    case 'percent':
      return 'range';
    case 'date':
    case 'datetime':
      return 'date';
    default:
      return 'none';
  }
}

/** The operators a long text's funnel offers, in its select's order — one root leaf each. */
export const TEXT_FUNNEL_OPS = [
  'contains',
  'not_contains',
  'starts',
  'ends',
  'empty',
  'not_empty'
] as const;

export type TextFunnelOp = (typeof TEXT_FUNNEL_OPS)[number];

/**
 * A funnel's state, as its form holds it. `values` — the ticked values (a facet's own: a code, an
 * account id, `true` / `false`) and whether "(Blank)" is ticked, kept or left out; `range` — the
 * two boxes' text; `date` — since, before or between, each a date literal (an ISO day or a token);
 * `text` — one operator and its box, as typed.
 */
export type FunnelState =
  | { test: 'values'; exclude: boolean; values: readonly unknown[]; blank: boolean }
  | { test: 'range'; min: string; max: string }
  | { test: 'date'; mode: 'since' | 'before' | 'between'; from: string; to: string }
  | { test: 'text'; op: TextFunnelOp; text: string };

function isTextFunnelOp(op: string): op is TextFunnelOp {
  return (TEXT_FUNNEL_OPS as readonly string[]).includes(op);
}

/** An empty funnel of the test. */
export function emptyFunnel(test: LeafTest): Nullable<FunnelState> {
  switch (test) {
    case 'values':
      return { test, exclude: false, values: [], blank: false };
    case 'range':
      return { test, min: '', max: '' };
    case 'date':
      return { test, mode: 'since', from: '', to: '' };
    case 'text':
      return { test, op: 'contains', text: '' };
    case 'none':
      return null;
  }
}

function listOf(value: unknown): unknown[] | null {
  return Array.isArray(value) ? value : null;
}

/** A value as a box holds it — a stored number as its digits, nothing as `''`. */
export function asBox(value: unknown): string {
  if (value === undefined || value === null) return '';
  return typeof value === 'string' ? value : String(value);
}

/** The bool funnel's three values as one set — `true`, `false`, and `null` for blank. */
function boolSetOf(nodes: readonly CriteriaNode[]): Nullable<Set<boolean | null>> {
  if (nodes.length !== 1 || nodes[0].type !== 'leaf') return null;
  const { op, value } = nodes[0];
  if (op === 'empty') return new Set([null]);
  if (op === 'not_empty') return new Set([true, false]);
  if (typeof value !== 'boolean') return null;
  if (op === 'eq') return new Set([value]);
  if (op === 'ne') return new Set([!value, null]);
  return null;
}

/**
 * The funnel's form, read off the column's own nodes — or `null` where the nodes are no shape the
 * funnel writes (`contains`, a `gt`, an `eq` on a day…), which the funnel then shows read-only
 * with "Set in Advanced…". The reverse of `funnelNodes`, shape for shape.
 */
export function funnelOf(nodes: readonly CriteriaNode[], kind: ListFieldKind): Nullable<FunnelState> {
  const test = leafTest(kind);
  if (nodes.length === 0) return emptyFunnel(test);
  if (test === 'values' && kind === 'bool') {
    const set = boolSetOf(nodes);
    if (!set) return null;
    const values = [true, false].filter((one) => set.has(one));
    return { test, exclude: false, values, blank: set.has(null) };
  }
  if (test === 'values') {
    const leaves = nodes.length === 1 && nodes[0].type === 'group' ? nodes[0].children : nodes;
    const or = nodes.length === 1 && nodes[0].type === 'group';
    if (!leaves.every((node): node is ICriteriaLeaf => node.type === 'leaf')) return null;
    const ops = leaves.map((leaf) => leaf.op).sort().join(' ');
    const valued = leaves.find((leaf) => leaf.op !== 'empty' && leaf.op !== 'not_empty');
    const single = valued?.op === 'eq' || valued?.op === 'ne' ? [valued.value] : null;
    const listed = valued ? (listOf(valued.value) ?? single) : [];
    if (listed === null) return null;
    const shapes: Record<string, { exclude: boolean; blank: boolean; or: boolean }> = {
      in: { exclude: false, blank: false, or: false },
      eq: { exclude: false, blank: false, or: false },
      empty: { exclude: false, blank: true, or: false },
      'empty in': { exclude: false, blank: true, or: true },
      not_in: { exclude: true, blank: false, or: false },
      ne: { exclude: true, blank: false, or: false },
      not_empty: { exclude: true, blank: true, or: false },
      'not_empty not_in': { exclude: true, blank: true, or: false }
    };
    const shape = shapes[ops];
    if (!shape || shape.or !== or) return null;
    return { test, exclude: shape.exclude, values: listed, blank: shape.blank };
  }
  if (nodes.length !== 1 || nodes[0].type !== 'leaf') return null;
  const { op, value } = nodes[0];
  if (test === 'text') {
    // An `eq` or `in` on a long text (Advanced's, or the migration's) is no shape the funnel writes.
    if (!isTextFunnelOp(op)) return null;
    if (operatorArity(op) === 'none') return value === undefined ? { test, op, text: '' } : null;
    return typeof value === 'string' ? { test, op, text: value } : null;
  }
  if (test === 'range') {
    if (op === 'gte') return { test, min: asBox(value), max: '' };
    if (op === 'lte') return { test, min: '', max: asBox(value) };
    const pair = listOf(value);
    if (op === 'between' && pair?.length === 2) return { test, min: asBox(pair[0]), max: asBox(pair[1]) };
    return null;
  }
  if (test === 'date') {
    if ((op === 'since' || op === 'before') && typeof value === 'string') {
      return { test, mode: op, from: value, to: '' };
    }
    const pair = listOf(value);
    if (op === 'between' && pair?.length === 2) {
      return { test, mode: 'between', from: asBox(pair[0]), to: asBox(pair[1]) };
    }
  }
  return null;
}

/** Whether a funnel's form filters anything — an Apply on an empty one clears the column. */
export function funnelFilters(state: FunnelState): boolean {
  switch (state.test) {
    case 'values':
      return state.values.length > 0 || state.blank;
    case 'range':
      return state.min.trim() !== '' || state.max.trim() !== '';
    case 'date':
      return state.from !== '' || (state.mode === 'between' && state.to !== '');
    case 'text':
      return operatorArity(state.op) === 'none' || state.text !== '';
  }
}

/**
 * The column's own nodes for a funnel's form (§3.4's table): ticked values `in` / `not_in` (a
 * bool's `eq` / `ne`); "(Blank)" ticked adds `empty` beside an include — the two in one `or`
 * group, "these values, or blank" — and `not_empty` beside an exclude, two root leaves, since
 * `not_in` already keeps the blanks (survey-api's §4); a range `gte` / `lte`, both `between`; a
 * date since, before or between. `[]` for a form that filters nothing.
 */
export function funnelNodes(field: string, kind: ListFieldKind, state: FunnelState): CriteriaNode[] {
  if (!funnelFilters(state)) return [];
  switch (state.test) {
    case 'values': {
      if (kind === 'bool') return boolNodes(field, state);
      const { values, blank, exclude } = state;
      if (values.length === 0) return [leafOf(field, exclude ? 'not_empty' : 'empty')];
      const listed = leafOf(field, exclude ? 'not_in' : 'in', [...values]);
      if (!blank) return [listed];
      if (exclude) return [listed, leafOf(field, 'not_empty')];
      return [newGroup('or', [listed, leafOf(field, 'empty')])];
    }
    case 'range': {
      const min = state.min.trim();
      const max = state.max.trim();
      if (min !== '' && max !== '') return [leafOf(field, 'between', [min, max])];
      return [min !== '' ? leafOf(field, 'gte', min) : leafOf(field, 'lte', max)];
    }
    case 'date': {
      if (state.mode === 'between') return [leafOf(field, 'between', [state.from, state.to])];
      return [leafOf(field, state.mode, state.from)];
    }
    case 'text':
      return [textFunnelLeaf(field, state)];
  }
}

/** A long text's funnel as its one leaf — the box's text sent as typed, or none for a blank test. */
function textFunnelLeaf(field: string, state: Extract<FunnelState, { test: 'text' }>): ICriteriaLeaf {
  return operatorArity(state.op) === 'none'
    ? leafOf(field, state.op)
    : leafOf(field, state.op, state.text);
}

/** A bool's funnel as one leaf: the included set of `true`, `false` and blank, an exclude read as
 *  its complement. Every one ticked, or none, filters nothing. */
function boolNodes(
  field: string,
  state: Extract<FunnelState, { test: 'values' }>
): CriteriaNode[] {
  const ticked = new Set<boolean | null>([
    ...state.values.filter((one): one is boolean => typeof one === 'boolean'),
    ...(state.blank ? [null] : [])
  ]);
  const all: (boolean | null)[] = [true, false, null];
  const included = all.filter((one) => ticked.has(one) !== state.exclude);
  const has = (one: boolean | null) => included.includes(one);
  // Every value excluded: a filter that matches no row, said as the two conditions it is.
  if (included.length === 0) return [leafOf(field, 'empty'), leafOf(field, 'not_empty')];
  if (included.length === 3) return [];
  if (included.length === 1) {
    const [only] = included;
    return [only === null ? leafOf(field, 'empty') : leafOf(field, 'eq', only)];
  }
  if (!has(null)) return [leafOf(field, 'not_empty')];
  return [leafOf(field, 'ne', !has(true))];
}

// ---- Words -------------------------------------------------------------------------------------

/** What the words read a field by: the catalogue's field, and — for a person's account ids — the
 *  email a facet or the assignees named it by, where the page holds one. */
export interface ICriteriaWords {
  fieldOf: (code: string) => IListField | undefined;
  personOf?: (accountId: string) => string | undefined;
}

/** The catalogue's fields by code — what `fieldOf` reads. */
export function fieldsByCode(catalogue: Nullable<IListFieldCatalogue>): ReadonlyMap<string, IListField> {
  const fields = new Map<string, IListField>();
  for (const group of catalogue?.groups ?? []) {
    for (const field of group.fields) fields.set(field.code, field);
  }
  return fields;
}

/** A field's name: its label, else its code as spelled. */
export function fieldLabel(words: ICriteriaWords, code: string): string {
  return words.fieldOf(code)?.label ?? code;
}

/** "A or B", "A, B or C" — a list in words with its last joint. */
function joined(parts: readonly string[], last: 'or' | 'and'): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} ${last} ${parts[parts.length - 1]}`;
}

/** One value in words, by the field's kind: an option's label, a number formatted as the column
 *  is, a date literal in words, an account by its email. A value of another shape as written. */
export function valueWords(words: ICriteriaWords, field: string, value: unknown): string {
  const known = words.fieldOf(field);
  if (typeof value === 'string' && (known?.kind === 'date' || known?.kind === 'datetime')) {
    return dateTokenLabel(value);
  }
  if (typeof value === 'string' && known?.kind === 'person') {
    return words.personOf?.(value) ?? value;
  }
  if (typeof value === 'string' && known && NUMBER_KINDS.includes(known.kind)) {
    const number = plainNumber(value);
    return number === null ? value : formatListValue(known, number);
  }
  if (known && known.kind !== 'person' && known.kind !== 'summary') {
    const said = formatListValue(known, value);
    if (said !== '') return said;
  }
  if (typeof value === 'string') return value;
  return value === undefined ? '' : JSON.stringify(value);
}

/** A list's values, one by one, a value holding a comma quoted so two never read as three. */
function valuesWords(words: ICriteriaWords, field: string, values: readonly unknown[]): string[] {
  return values.map((value) => {
    const said = valueWords(words, field, value);
    return said.includes(',') ? `“${said}”` : said;
  });
}

/** Whether a leaf has what its operator needs — a field, an operator, and a value where one is
 *  taken. An unfinished one is said so, never guessed at. */
export function isLeafComplete(leaf: ICriteriaLeaf): boolean {
  if (leaf.field === '' || leaf.op === '') return false;
  const arity = operatorArity(leaf.op);
  if (arity === 'none') return true;
  const { value } = leaf;
  if (arity === 'list') return Array.isArray(value) && value.length > 0;
  if (arity === 'pair') {
    const filled = (one: unknown) => one !== '' && one !== undefined;
    return Array.isArray(value) && value.length === 2 && value.every(filled);
  }
  return value !== undefined && value !== '';
}

/** A date's `eq` in words: on a day, "is" a single day a token names, "is in" a token's span. */
function dateEqWord(value: unknown): string {
  if (typeof value !== 'string') return 'is';
  const token = readDateToken(value);
  if (token === null) return 'is';
  if (token.kind === 'day') return 'is on';
  if (token.kind === 'word' && ['today', 'yesterday', 'tomorrow'].includes(token.token)) return 'is';
  if (token.kind === 'n' && token.form.startsWith('n_')) return 'is';
  return 'is in';
}

/**
 * A condition in words (§2, §3.4): "State is NY or CT", "Travel is not Eligible", "Created since
 * the cycle opened", "39a Individual AUM between $1,000,000 and $5,000,000". A field, operator or
 * value this app has no word for is said as spelled; an unfinished condition says what it lacks.
 */
export function describeLeaf(leaf: ICriteriaLeaf, words: ICriteriaWords): string {
  if (leaf.field === '') return 'A condition not filled in yet';
  const name = fieldLabel(words, leaf.field);
  if (leaf.op === '') return `${name} — not filled in yet`;
  const kind = words.fieldOf(leaf.field)?.kind;
  const { op, value } = leaf;
  const arity = operatorArity(op);
  if (arity === 'none') return `${name} ${operatorLabel(op)}`;
  if (!isLeafComplete(leaf)) return `${name} ${operatorLabel(op)} …`;
  if (arity === 'list' && Array.isArray(value)) {
    const listed = valuesWords(words, leaf.field, value);
    if (op === 'in' && kind !== 'multi_enum') return `${name} is ${joined(listed, 'or')}`;
    if (op === 'in') return `${name} includes any of ${joined(listed, 'or')}`;
    if (op === 'not_in') return `${name} is none of ${listed.join(', ')}`;
    return `${name} ${operatorLabel(op)} ${joined(listed, 'and')}`;
  }
  if (arity === 'pair' && Array.isArray(value)) {
    const [low, high] = value.map((one) => valueWords(words, leaf.field, one));
    return `${name} between ${low} and ${high}`;
  }
  const said = valueWords(words, leaf.field, value);
  if (op === 'eq' && (kind === 'date' || kind === 'datetime')) return `${name} ${dateEqWord(value)} ${said}`;
  if (op === 'contains' || op === 'not_contains' || op === 'starts' || op === 'ends') {
    return `${name} ${operatorLabel(op)} “${said}”`;
  }
  return `${name} ${operatorLabel(op)} ${said}`;
}

/** What a node the words cannot say is called — a raw node. */
export const UNSAYABLE = 'A condition the words cannot say';

/** A group as one chip: "3 conditions, any of them" — "every row" for an empty one. */
export function describeGroup(group: ICriteriaGroup): string {
  const n = group.children.length;
  if (n === 0) return 'every row';
  const count = `${n} ${n === 1 ? 'condition' : 'conditions'}`;
  return `${count}, ${group.op === 'and' ? 'all' : 'any'} of them`;
}

/** A node in words — a leaf's sentence, a group's count, a raw node's "cannot say". */
export function describeNode(node: CriteriaNode, words: ICriteriaWords): string {
  if (node.type === 'leaf') return describeLeaf(node, words);
  if (node.type === 'group') return describeGroup(node);
  return UNSAYABLE;
}

/** A funnel's state in words, for its column's chip: "State is NY or CT, or blank", "Travel is
 *  not Eligible", "AUM at least $1,000,000", "Created since the last 30 days". */
function describeFunnel(
  field: string,
  kind: ListFieldKind,
  state: FunnelState,
  words: ICriteriaWords
): string {
  const name = fieldLabel(words, field);
  switch (state.test) {
    case 'values': {
      const listed = valuesWords(words, field, state.values);
      const all = state.blank ? [...listed, 'blank'] : listed;
      if (kind === 'multi_enum' && !state.exclude && !state.blank) {
        return `${name} includes any of ${joined(all, 'or')}`;
      }
      return `${name} ${state.exclude ? 'is not' : 'is'} ${joined(all, 'or')}`;
    }
    case 'range': {
      const min = valueWords(words, field, state.min);
      const max = valueWords(words, field, state.max);
      if (state.min.trim() !== '' && state.max.trim() !== '') return `${name} between ${min} and ${max}`;
      return state.min.trim() !== '' ? `${name} at least ${min}` : `${name} at most ${max}`;
    }
    case 'date': {
      const from = dateTokenLabel(state.from);
      if (state.mode === 'between') return `${name} between ${from} and ${dateTokenLabel(state.to)}`;
      return `${name} ${state.mode} ${from}`;
    }
    case 'text':
      // The criteria's own sentence, as Advanced… would say the same leaf.
      return describeLeaf(textFunnelLeaf(field, state), words);
  }
}

/**
 * Where a chip's press leads (§3.4): a column's chip to that column's funnel, the name box's to the
 * box, any other — a nested group, a node the words cannot say — to *Advanced…* on its node.
 */
export type ChipDoor = 'funnel' | 'name' | 'advanced';

/** A chip of the filter (§3.4): the nodes it stands for, its words, and where its press leads. */
export interface IFilterChip {
  key: string;
  nodeIds: readonly string[];
  words: string;
  field: Nullable<string>;
  door: ChipDoor;
}

/**
 * The root's chips, in its order: a column's own nodes as one chip in words (its funnel's), the
 * name box's leaf as its own, any other leaf as its sentence, a nested group as "n conditions, any
 * of them", a raw node as "a condition the words cannot say". An `or` root is one chip — the whole
 * filter is one group then. Each chip's ✕ removes its nodes; `Filters (n)` counts the chips.
 */
export function rootChips(root: ICriteriaGroup, words: ICriteriaWords): IFilterChip[] {
  if (root.children.length === 0) return [];
  if (root.op === 'or') {
    return [
      {
        key: root.id,
        nodeIds: root.children.map((child) => child.id),
        words: describeGroup(root),
        field: null,
        door: 'advanced'
      }
    ];
  }
  const chips: IFilterChip[] = [];
  const seen = new Set<string>();
  for (const child of root.children) {
    if (seen.has(child.id)) continue;
    if (isNameBox(child)) {
      chips.push({
        key: child.id,
        nodeIds: [child.id],
        words: describeNode(child, words),
        field: NAME_FIELD,
        door: 'name'
      });
      continue;
    }
    const field = child.type === 'leaf' ? child.field : ownFieldOfGroup(child);
    if (field !== null && field !== '') {
      const own = root.children.filter((node) => isColumnOwn(node, field));
      own.forEach((node) => seen.add(node.id));
      const kind = words.fieldOf(field)?.kind;
      const state = kind ? funnelOf(own, kind) : null;
      const said =
        state && kind && funnelFilters(state)
          ? describeFunnel(field, kind, state, words)
          : own.map((node) => describeNode(node, words)).join(' and ');
      // Keyed by the column, not a node: a funnel's apply writes new nodes, and a chip remounted
      // under it would lose the focus the funnel hands back to it.
      chips.push({
        key: `column:${field}`,
        nodeIds: own.map((node) => node.id),
        words: said,
        field,
        door: 'funnel'
      });
      continue;
    }
    chips.push({
      key: child.id,
      nodeIds: [child.id],
      words: describeNode(child, words),
      field: null,
      door: 'advanced'
    });
  }
  return chips;
}

/** The field an `or` group of one column's leaves is about — the funnel's "or blank" group. */
function ownFieldOfGroup(node: CriteriaNode): Nullable<string> {
  if (node.type !== 'group' || node.op !== 'or' || node.children.length === 0) return null;
  const [first] = node.children;
  if (first.type !== 'leaf') return null;
  return isColumnOwn(node, first.field) ? first.field : null;
}

/** The root without the given nodes — a chip's ✕. */
export function withoutNodes(root: ICriteriaGroup, ids: readonly string[]): ICriteriaGroup {
  return ids.reduce((tree, id) => removeNode(tree, id), root);
}

// ---- Refusals ----------------------------------------------------------------------------------

/** A problem survey-api answered — `target` its JSON path, `reason` its words. */
export interface ICriteriaProblem {
  target: string;
  reason: string;
}

/** The problems laid out on the tree: by node id, and those no node carries (a sort's, a
 *  column's, a path past this tree — said whole by the caller, never guessed onto a row). */
export interface IPlacedCriteriaProblems {
  byNode: ReadonlyMap<string, readonly string[]>;
  unplaced: readonly ICriteriaProblem[];
}

export const NO_PROBLEMS: IPlacedCriteriaProblems = { byNode: new Map(), unplaced: [] };

/**
 * Each problem on the node whose path it names (§3.4, `placeProblems`' precedent): a leaf carries
 * its own path and `.field`, `.op`, `.value` under it; a group its own and `.op`, `.children`; the
 * root also `.version`. A raw node takes everything under its path. `paths` are the ones of the
 * tree as it was sent (`criteriaPaths`) — a path names a position, and the tree may have moved
 * since; `forgetChanged` keeps them on their nodes from then on.
 */
export function placeProblems(
  problems: readonly ICriteriaProblem[],
  root: ICriteriaGroup,
  paths: ReadonlyMap<string, string> = criteriaPaths(root)
): IPlacedCriteriaProblems {
  const owners = new Map<string, string>();
  const raws: { path: string; id: string }[] = [];
  const visit = (node: CriteriaNode) => {
    const path = paths.get(node.id);
    if (path === undefined) return;
    owners.set(path, node.id);
    if (node.type === 'leaf') {
      for (const key of ['field', 'op', 'value']) owners.set(`${path}.${key}`, node.id);
    } else if (node.type === 'group') {
      for (const key of ['op', 'children', 'version']) owners.set(`${path}.${key}`, node.id);
      node.children.forEach(visit);
    } else {
      raws.push({ path, id: node.id });
    }
  };
  visit(root);
  const byNode = new Map<string, string[]>();
  const unplaced: ICriteriaProblem[] = [];
  for (const problem of problems) {
    const under = (path: string) =>
      problem.target.startsWith(`${path}.`) || problem.target.startsWith(`${path}[`);
    const owner = owners.get(problem.target) ?? raws.find((one) => under(one.path))?.id;
    if (owner === undefined) unplaced.push(problem);
    else byNode.set(owner, [...(byNode.get(owner) ?? []), problem.reason]);
  }
  return { byNode, unplaced };
}

/**
 * The placed problems once the tree moved under them: a removed node takes its problems with it,
 * and a leaf whose field or operator changed drops them — they were said of another question. A
 * value edited keeps its problem until the next answer, which is survey-api's to give.
 */
export function forgetChanged(
  placed: IPlacedCriteriaProblems,
  before: ICriteriaGroup,
  after: ICriteriaGroup
): IPlacedCriteriaProblems {
  const was = new Map(leavesOf(before).map((leaf) => [leaf.id, leaf]));
  const byNode = new Map(
    [...placed.byNode].filter(([id]) => {
      const now = findNode(after, id);
      if (now === undefined) return false;
      const old = was.get(id);
      return !old || (now.type === 'leaf' && now.field === old.field && now.op === old.op);
    })
  );
  return { byNode, unplaced: placed.unplaced };
}

// ---- Formatting --------------------------------------------------------------------------------

/** Blank, as survey-api's §4 says: no value, `null`, blank text, an empty list. */
function isBlank(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  return Array.isArray(value) && value.length === 0;
}

/** An instant's day in the business zone survey-api resolves dates in (`America/New_York`). */
function businessDay(instant: string): Nullable<string> {
  const at = new Date(instant);
  if (Number.isNaN(at.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(at);
  return /^\d{4}-\d{2}-\d{2}$/.test(parts) ? parts : null;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** A rule's day literal — an ISO day only. A token ("the last 30 days", "the cycle opened") is
 *  survey-api's to resolve, against its clock and the row's cycle, and is never resolved here: a
 *  rule on one matches nothing (the design's §3.8 says the screen evaluates the operators over the
 *  value as the row holds it; a second resolver of the tokens would be a second binder). */
function ruleDay(value: unknown): Nullable<string> {
  return typeof value === 'string' && ISO_DAY.test(value) ? value : null;
}

function ruleNumber(value: unknown): Nullable<number> {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return typeof value === 'string' ? plainNumber(value) : null;
}

/** Whether a cell's value — as the row holds it — meets a comparison; `undefined` where the
 *  value or the rule's literal is of the wrong type, which matches nothing, negations included. */
function compares(kind: ListFieldKind, op: string, cell: unknown, literal: unknown): boolean | undefined {
  if (kind === 'date' || kind === 'datetime') {
    const day = typeof cell === 'string' ? (kind === 'date' ? cell.slice(0, 10) : businessDay(cell)) : null;
    if (day === null || !ISO_DAY.test(day)) return undefined;
    if (op === 'between') {
      const pair = listOf(literal);
      const low = pair ? ruleDay(pair[0]) : null;
      const high = pair ? ruleDay(pair[1]) : null;
      return low === null || high === null ? undefined : day >= low && day <= high;
    }
    const at = ruleDay(literal);
    if (at === null) return undefined;
    if (op === 'eq') return day === at;
    if (op === 'since') return day >= at;
    if (op === 'before') return day < at;
    return undefined;
  }
  if (NUMBER_KINDS.includes(kind)) {
    if (typeof cell !== 'number') return undefined;
    if (op === 'between') {
      const pair = listOf(literal);
      const low = pair ? ruleNumber(pair[0]) : null;
      const high = pair ? ruleNumber(pair[1]) : null;
      return low === null || high === null ? undefined : cell >= low && cell <= high;
    }
    if (op === 'in') {
      const listed = listOf(literal)?.map(ruleNumber);
      return listed && listed.every((one) => one !== null) ? listed.includes(cell) : undefined;
    }
    const n = ruleNumber(literal);
    if (n === null) return undefined;
    const answers: Record<string, boolean> = {
      eq: cell === n,
      ne: cell !== n,
      gt: cell > n,
      gte: cell >= n,
      lt: cell < n,
      lte: cell <= n
    };
    return answers[op];
  }
  if (kind === 'bool') {
    if (typeof cell !== 'boolean' || typeof literal !== 'boolean') return undefined;
    if (op === 'eq') return cell === literal;
    if (op === 'ne') return cell !== literal;
    return undefined;
  }
  if (kind === 'multi_enum') {
    if (!Array.isArray(cell)) return undefined;
    const listed = listOf(literal);
    if (!listed) return undefined;
    if (op === 'in') return listed.some((one) => cell.includes(one));
    if (op === 'all_of') return listed.every((one) => cell.includes(one));
    if (op === 'not_in') return !listed.some((one) => cell.includes(one));
    return undefined;
  }
  // text and long_text ignore case for every operator (§4, SR-417 — as survey-api's filter does);
  // an enum's or a person's code is matched as stored, and the pattern operators ignore case for
  // every kind. A long text's line breaks are characters like any other.
  if (typeof cell !== 'string') return undefined;
  const folds = kind === 'text' || kind === 'long_text';
  const fold = (value: string) => (folds ? value.toLowerCase() : value);
  if (op === 'in' || op === 'not_in') {
    const listed = listOf(literal);
    if (!listed || !listed.every((one) => typeof one === 'string')) return undefined;
    const found = (listed as string[]).some((one) => fold(one) === fold(cell));
    return op === 'in' ? found : !found;
  }
  if (typeof literal !== 'string') return undefined;
  const text = cell.toLowerCase();
  const sought = literal.toLowerCase();
  const answers: Record<string, boolean> = {
    eq: fold(cell) === fold(literal),
    ne: fold(cell) !== fold(literal),
    contains: text.includes(sought),
    not_contains: !text.includes(sought),
    starts: text.startsWith(sought),
    ends: text.endsWith(sought)
  };
  return answers[op];
}

/** The operators that also match a row with no value (survey-api's §4). */
const MATCH_BLANK: readonly string[] = ['empty', 'ne', 'not_in', 'not_contains'];

/**
 * Whether a cell's value meets a view's formatting rule (§3.8) — the one evaluation on these
 * screens. The grammar's operators over the value as the row holds it, by the field's kind;
 * blanks match `empty`, `ne`, `not_in` and `not_contains` and nothing else; a value or a literal of
 * the wrong type matches nothing, negations included; an operator the kind does not take matches
 * nothing; a rule with no field or operator (a migrated part) matches nothing.
 */
export function evaluateFormatting(
  rule: Pick<IListFormattingRule, 'field' | 'op' | 'value'>,
  kind: ListFieldKind | undefined,
  cell: unknown
): boolean {
  const { op } = rule;
  if (!rule.field || !op || kind === undefined || !operatorsFor(kind).includes(op)) return false;
  if (isBlank(cell)) return MATCH_BLANK.includes(op);
  if (op === 'empty') return false;
  if (op === 'not_empty') return true;
  return compares(kind, op, cell, rule.value) ?? false;
}

// ---- Changed — not saved -----------------------------------------------------------------------

/** What of a view the screen can change and Save view sends (§3.3): the query, the page size and
 *  the formatting rules. `viewId` is a read's, never compared. */
export interface IViewScreen {
  query: IListQuery;
  pageSize: Nullable<number>;
  formattingRules: readonly IListFormattingRule[];
  /** The pinned conditions on screen (SR-412); a view without them compares as none. */
  pinned?: Nullable<IListCriteriaGroup>;
}

/** A pinned tree as compared: none for an empty one. */
function pinnedParts(pinned: Nullable<IListCriteriaGroup> | undefined): unknown {
  return pinned && pinned.children.length > 0 ? withoutNullValues(pinned) : null;
}

/**
 * The tree a list reads (SR-412): the view's pinned conditions ANDed with the screen's own — the
 * pinned group alone, or the screen's, where the other holds nothing. Never sent as a view's
 * criteria: a save keeps the two apart.
 */
export function withPinned(pinned: ICriteriaGroup, root: ICriteriaGroup): ICriteriaGroup {
  if (pinned.children.length === 0) return root;
  if (root.children.length === 0) return pinned;
  return { id: 'pinned-and', type: 'group', op: 'and', children: [pinned, root] };
}

/** JSON with every object's keys sorted — survey-api stores a view as `jsonb`, which hands its
 *  members back reordered, and an order is no change. Arrays keep their order: it is the view's. */
function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (isRecord(value)) {
    const keys = Object.keys(value)
      .filter((key) => value[key] !== undefined)
      .sort();
    return `{${keys.map((key) => `${JSON.stringify(key)}:${canonical(value[key])}`).join(',')}}`;
  }
  return JSON.stringify(value ?? null);
}

/** A criteria node with a leaf's `value: null` left out, as `readCriteria` reads it — survey-api's
 *  seeds store `empty` and `not_empty` leaves with one, which the screen sends back without. */
function withoutNullValues(node: unknown): unknown {
  if (!isRecord(node)) return node;
  const { value, children, ...rest } = node;
  return {
    ...rest,
    ...(value === null || value === undefined ? {} : { value }),
    ...(Array.isArray(children) ? { children: children.map(withoutNullValues) } : children === undefined ? {} : { children })
  };
}

/** The parts of a query a view saves, absent and empty read alike — `null` columns and `[]` are one
 *  thing to a reader, as an absent criteria and an empty root are, and a leaf's `null` value and
 *  none. */
function savedParts(query: IListQuery): unknown {
  const criteria =
    query.criteria && query.criteria.children.length > 0 ? withoutNullValues(query.criteria) : null;
  const scope = query.scope ?? {};
  return {
    survey: query.survey,
    scope: { path: scope.path ?? null, mine: scope.mine ?? null, cycle: scope.cycle ?? null },
    criteria,
    columns: query.columns ?? [],
    sorts: (query.sorts ?? []).map((sort) => ({ field: sort.field, dir: sort.dir ?? 'asc' })),
    groupBy: (query.groupBy ?? []).map((level) => ({ field: level.field, dir: level.dir ?? 'asc' }))
  };
}

/**
 * Whether the screen differs from the view as opened — *Changed — not saved*, and the `*` before
 * its name (§3.3). The criteria compared as the wire spells them (`writeCriteria`'s output on the
 * screen's side), so a tree read and written back unchanged is no change.
 */
export function isChanged(
  view: Pick<ISavedView, 'query' | 'pageSize' | 'formattingRules' | 'pinned'>,
  screen: IViewScreen
): boolean {
  return (
    canonical(savedParts(view.query)) !== canonical(savedParts(screen.query)) ||
    canonical(pinnedParts(view.pinned)) !== canonical(pinnedParts(screen.pinned)) ||
    (view.pageSize ?? null) !== (screen.pageSize ?? null) ||
    canonical(view.formattingRules ?? []) !== canonical(screen.formattingRules)
  );
}
